import type { Permissao } from '@baiocchi/shared'
import { Botao, EstadoCarregando, EstadoSemPermissao } from '@baiocchi/ui'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { useSessao } from './contexto-sessao'

/**
 * Rotas que **não** são destino de retorno pós-login.
 *
 * `/sair` é de passagem: entrar nela é pedir para encerrar a sessão. Guardá-la
 * como `destino` criava um laço fechado e reprodutível — sair e entrar de novo
 * deslogava o usuário na hora:
 *
 *   1. clique em "Sair" → SPA vai para `/sair`, dentro deste portão;
 *   2. `PaginaSair` limpa a sessão → este componente re-renderiza sem
 *      autenticação e redireciona para `/entrar` com `destino: '/sair'`;
 *   3. o login seguinte obedece o `destino` e volta para `/sair`;
 *   4. `PaginaSair` monta outra vez e encerra a sessão recém-criada.
 *
 * O passo 2 corria com o `navegar('/entrar')` do próprio `PaginaSair`, que não
 * leva estado — por isso o defeito era intermitente e passava despercebido em uso
 * manual. Encontrado pelo roteiro de demonstração, que troca de usuário seis
 * vezes seguidas (`docs/release/17-demo-e2e.md`).
 */
const ROTAS_DE_PASSAGEM = new Set(['/sair', '/entrar'])

/**
 * Portão de rota autenticada.
 *
 * Guarda o destino em `state.destino` para devolver o usuário ao lugar onde ele
 * estava depois do login: um corretor que abre o link de um lead recebido no
 * WhatsApp com a sessão vencida precisa cair no lead, não na home.
 */
export function RotaAutenticada() {
  const { autenticado, carregando, usuario } = useSessao()
  const local = useLocation()

  if (carregando) return <EstadoCarregando descricao="Verificando sua sessão…" />

  if (!autenticado) {
    const destino = ROTAS_DE_PASSAGEM.has(local.pathname)
      ? undefined
      : local.pathname + local.search
    return <Navigate to="/entrar" replace state={destino ? { destino } : null} />
  }

  /**
   * RN-06: senha definida por terceiro obriga troca no primeiro acesso. A API
   * responde `403 TROCA_SENHA_OBRIGATORIA` em tudo que não seja a troca, então
   * mandar o usuário para outra tela produziria uma sequência de erros.
   */
  if (usuario?.deveTrocarSenha && !local.pathname.startsWith('/perfil')) {
    return <Navigate to="/perfil?trocar-senha=1" replace />
  }

  return <Outlet />
}

/**
 * Portão por permissão.
 *
 * Mostra a tela de acesso negado em vez de redirecionar: quem chegou pelo link de
 * um colega precisa entender **por que** não vê o conteúdo. Redirecionar para a
 * home faz o usuário achar que o link estava errado e pedir de novo.
 */
export function RotaComPermissao({
  permissoes,
  modo = 'todas',
}: {
  permissoes: ReadonlyArray<Permissao>
  modo?: 'todas' | 'qualquer'
}) {
  const { pode, podeQualquer } = useSessao()
  const navegar = useNavigate()

  const liberado = modo === 'todas' ? pode(...permissoes) : podeQualquer(...permissoes)

  if (!liberado) {
    return (
      <EstadoSemPermissao
        acao={
          <Botao variante="contorno" onClick={() => navegar('/')}>
            Voltar para o início
          </Botao>
        }
      />
    )
  }

  return <Outlet />
}
